import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Hardware } from "../request/request";

export interface HardwareViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  hardware: Hardware | null;
  onClose: () => void;
  getTypeHardwareString: (value: string | { name: string } | null) => string;
}

export const HardwareViewModal: React.FC<HardwareViewModalProps> = ({
  isOpen,
  isLoading,
  hardware,
  onClose,
  getTypeHardwareString,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="View Hardware Details"
      customWidth="max-w-6xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 10 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="40%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        hardware && (
          <div className="space-y-6">
            {/* Basic Information */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="ID"
                  value={String(hardware.id ?? "")}
                  readOnly
                />
                <GenericInput
                  label="Data Center ID"
                  value={hardware.datacenterId ?? ""}
                  readOnly
                />
                <GenericInput
                  label="Type"
                  value={getTypeHardwareString(hardware.typeHardware ?? "")}
                  readOnly
                />
                <GenericInput
                  label="SubType"
                  value={getTypeHardwareString(hardware.subtypeHardware ?? "")}
                  readOnly
                />
                <GenericInput
                  label="Serial Number"
                  value={hardware.serialNumber ?? ""}
                  readOnly
                />
                <GenericInput
                  label="Model"
                  value={hardware.model ?? ""}
                  readOnly
                />
                <GenericInput
                  label="Vendor"
                  value={hardware.vendor ?? ""}
                  readOnly
                />
                <GenericInput
                  label="Hostname"
                  value={hardware.hostname ?? ""}
                  readOnly
                />
                <GenericInput
                  label="Installed Date"
                  value={hardware.installedDate ?? ""}
                  readOnly
                />
                <GenericInput
                  label="End of Life Date"
                  value={hardware.endOfLifeDate ?? ""}
                  readOnly
                />
              </div>
            </GenericCard>

            {/* Infrastructure Information - Only for non-Module types */}
            {hardware.typeHardware !== "Module" && (
              <GenericCard isCollapsible={false} className="p-5">
                <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                  Infrastructure Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <GenericInput
                    label="Rack ID"
                    value={hardware.rackId ?? ""}
                    readOnly
                  />
                  <GenericInput
                    label="Rack Unit"
                    value={
                      hardware.rackUnit !== undefined
                        ? String(hardware.rackUnit)
                        : ""
                    }
                    readOnly
                  />
                  <GenericInput
                    label="Phase"
                    value={hardware.phase ?? ""}
                    readOnly
                  />
                  <GenericInput
                    label="Template"
                    value={hardware.template ?? ""}
                    readOnly
                  />
                </div>
              </GenericCard>
            )}
          </div>
        )
      )}

      <div className="flex justify-end mt-6">
        <GenericButton label="Close" variant="secondary" onClick={onClose} />
      </div>
    </GenericModal>
  );
};
